import React from "react";
import { Empty } from "antd";
import Chart from "react-apexcharts";
import moment from "moment";
import {
  AiOutlineFileText,
  AiOutlineRise,
  AiOutlineSolution,
  AiOutlineCheckCircle,
} from "react-icons/ai";
import { Card, CardTitle, KpiStatTile, SectionShell } from "../blocks";
import {
  ADMISSION_STATUS_META,
  INK,
  SECTION_COLORS,
  axisLabelStyle,
  baseChart,
  baseGrid,
} from "../chartTheme";

const ACCENT = SECTION_COLORS.admissions;

const AdmissionsSection = ({ overview, admissions }) => {
  const admissionSeries = ADMISSION_STATUS_META.map(
    (s) => admissions.byStatus?.[s.key] || 0
  );
  const hasAdmissionData = admissionSeries.some((v) => v > 0);
  const totalApplications = admissionSeries.reduce((a, b) => a + b, 0);
  const approvedCount = admissions.byStatus?.approved || 0;
  const conversionRate =
    totalApplications > 0
      ? Math.round((approvedCount / totalApplications) * 100)
      : 0;

  // Fixed trailing 6-month window; months without applications render as zero
  const trendMonths = Array.from({ length: 6 }, (_, i) => {
    const m = moment().startOf("month").subtract(5 - i, "months");
    const row = (admissions.monthlyTrend || []).find(
      (t) => t.month === m.format("YYYY-MM")
    );
    return { label: m.format("MMM"), count: row ? row.count : 0 };
  });
  const hasTrendData = trendMonths.some((m) => m.count > 0);

  const admissionsBar = {
    options: {
      chart: baseChart("iskool360-admissions", "bar"),
      colors: ADMISSION_STATUS_META.map((s) => s.color),
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "45%",
          borderRadius: 4,
          distributed: true,
          dataLabels: { position: "top" },
        },
      },
      // One label per bar on a 4-bar funnel is selective labeling; ink at the
      // bar end, not white-in-bar, so it stays legible on short bars too.
      dataLabels: {
        enabled: true,
        offsetX: 20,
        style: { fontSize: "12px", fontWeight: 700, colors: [INK.primary] },
      },
      legend: { show: false },
      xaxis: {
        categories: ADMISSION_STATUS_META.map((s) => s.label),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: axisLabelStyle,
      },
      tooltip: { y: { formatter: (v) => `${v} applications` } },
      grid: baseGrid,
    },
    series: [{ name: "Applications", data: admissionSeries }],
  };

  const trendColumns = {
    options: {
      chart: baseChart("iskool360-admissions-trend", "bar"),
      colors: [ACCENT],
      plotOptions: { bar: { columnWidth: "28%", borderRadius: 4 } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: trendMonths.map((m) => m.label),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: axisLabelStyle,
      },
      yaxis: { labels: { formatter: (v) => Math.round(v) } },
      tooltip: { y: { formatter: (v) => `${v} applications` } },
      grid: baseGrid,
    },
    series: [{ name: "Applications", data: trendMonths.map((m) => m.count) }],
  };

  return (
    <SectionShell
      id="admissions"
      title="Admissions"
      subtitle="Applications pipeline"
      accentColor={ACCENT}
    >
      <div className="k360-grid k360-cols-2-4 k360-mb">
        <KpiStatTile
          title="Pending"
          value={overview.pendingApplications}
          caption="Awaiting review"
          icon={<AiOutlineFileText size={17} />}
          chipClass="bg-amber-50 text-amber-600"
          accentColor={ACCENT}
        />
        <KpiStatTile
          title="This Month"
          value={overview.applicationsThisMonth}
          caption="New applications received"
          icon={<AiOutlineRise size={17} />}
          chipClass="bg-sky-50 text-sky-700"
          accentColor={ACCENT}
        />
        <KpiStatTile
          title="Total"
          value={totalApplications}
          caption="All applications on record"
          icon={<AiOutlineSolution size={17} />}
          chipClass="bg-violet-50 text-violet-600"
          accentColor={ACCENT}
        />
        <KpiStatTile
          title="Conversion Rate"
          value={`${conversionRate}%`}
          caption={`${approvedCount} approved of ${totalApplications}`}
          icon={<AiOutlineCheckCircle size={17} />}
          chipClass="bg-emerald-50 text-emerald-600"
          captionClass="text-emerald-600 font-semibold"
          accentColor={ACCENT}
        />
      </div>

      <div className="k360-grid k360-cols-2">
        <Card accentColor={ACCENT}>
          <CardTitle
            title="Applications by Status"
            subtitle={`${totalApplications} total applications`}
          />
          {hasAdmissionData ? (
            <Chart
              options={admissionsBar.options}
              series={admissionsBar.series}
              type="bar"
              height={260}
            />
          ) : (
            <Empty description="No applications yet" className="py-12" />
          )}
        </Card>

        <Card accentColor={ACCENT}>
          <CardTitle
            title="Applications Trend"
            subtitle="New applications — last 6 months"
          />
          {hasTrendData ? (
            <Chart
              options={trendColumns.options}
              series={trendColumns.series}
              type="bar"
              height={260}
            />
          ) : (
            <Empty
              description="No applications in the last 6 months"
              className="py-12"
            />
          )}
        </Card>
      </div>
    </SectionShell>
  );
};

export default AdmissionsSection;
